import { render, showToast, showApiError } from '../main';
import { API_ERR_START_CHAT } from '../utils/app-strings';
import { matchesApi, chatsApi } from '../api';
import { navigate, getCurrentPath } from '../router';
import { Match, store } from '../state';
import socket from '../socket';
import {
  escapeHtml,
  truncate,
  formatTimeAgo,
  formatDistance,
  getUnits,
  loadingSpinner,
  emptyState,
  errorState,
} from '../utils';
import { renderSortMenu, getSortLabel, setupSortMenuHandlers, type SortRow } from '../components/sort-menu';
import { renderListItem } from '../components/list-item';

/** Strip {{QA_REPLY|...}} and {{IMG|...}} tags, returning display text */
function stripQaReplyPrefix(text: string): string {
  // Strip IMG tags first
  let cleaned = text.replace(/\{\{IMG\|[^}]+\}\}/g, '');
  // Strip QA_REPLY prefix
  const m = /^\{\{QA_REPLY\|\d+\|[^}]+\}\}([\s\S]*)$/.exec(cleaned);
  if (m) cleaned = m[1];
  // If everything was stripped (image-only message), show [Photo]
  return cleaned.trim() || '[Photo]';
}

type SortField = 'name' | 'age' | 'recent' | 'distance' | 'messages' | 'answered';
type SortDirection = 'asc' | 'desc';

// Sort options grouped in pairs for display
const SORT_ROWS: SortRow[][] = [
  [
    { key: 'recent-desc', icon: 'clock', label: 'Recent' },
    { key: 'recent-asc', icon: 'history', label: 'Oldest' },
  ],
  [
    { key: 'distance-asc', icon: 'location-arrow', label: 'Nearest' },
    { key: 'distance-desc', icon: 'map-marker-alt', label: 'Furthest' },
  ],
  [
    { key: 'name-asc', icon: 'sort-alpha-down', label: 'Name A-Z' },
    { key: 'name-desc', icon: 'sort-alpha-up', label: 'Name Z-A' },
  ],
  [
    { key: 'age-asc', icon: 'user', label: 'Youngest' },
    { key: 'age-desc', icon: 'user-clock', label: 'Age Oldest' },
  ],
  [
    { key: 'messages-desc', icon: 'comments', label: 'Longest' },
    { key: 'messages-asc', icon: 'comment', label: 'Shortest' },
  ],
  [
    { key: 'answered-desc', icon: 'check-circle', label: 'Most Answered' },
    { key: 'answered-asc', icon: 'circle', label: 'Least Answered' },
  ],
];

function getCurrentSortKey(): string {
  return `${currentSort}-${currentDirection}`;
}

function getCurrentSortLabel(): string {
  return getSortLabel(SORT_ROWS, getCurrentSortKey());
}

let currentSort: SortField = 'recent';
let currentDirection: SortDirection = 'desc';
let matches: Match[] = [];
let savedScrollPosition: number | null = null;

// Track typing state: userId -> typing text
const typingState: Map<string, string> = new Map();

// Track pending typing clear timeouts: userId -> timeoutId
// This delays clearing typing state to avoid flash when message arrives right after
const typingClearTimeouts: Map<string, number> = new Map();

// Interval for updating time display
let timeUpdateInterval: number | null = null;

export async function renderMatches(): Promise<void> {
  const currentKey = getCurrentSortKey();

  render(`
    <div class="view-frame">
      <div class="browse-header" style="justify-content: flex-end;">
        <button class="browse-pill" id="sort-btn">
          <span class="browse-pill-main">Sort <i class="fas fa-sort"></i></span>
          <span class="browse-pill-label" id="sort-label">${getCurrentSortLabel()}</span>
        </button>
      </div>
      ${renderSortMenu(SORT_ROWS, currentKey)}
      <div class="view-scroll" id="friends-list">
        ${loadingSpinner()}
      </div>
    </div>
  `);

  setupSortHandlers();
  setupSocketListeners();
  startTimeUpdateInterval();
  await loadMatches();

  // Restore scroll position if returning from chat
  if (savedScrollPosition !== null) {
    const scrollTo = savedScrollPosition;
    savedScrollPosition = null;
    requestAnimationFrame(() => {
      const list = document.getElementById('friends-list');
      if (list) list.scrollTop = scrollTo;
    });
  }
}

function setupSortHandlers(): void {
  setupSortMenuHandlers(
    getCurrentSortKey,
    (newKey) => {
      const [field, direction] = newKey.split('-') as [SortField, SortDirection];
      currentSort = field;
      currentDirection = direction;
      renderList();
      const list = document.getElementById('friends-list');
      if (list) list.scrollTop = 0;
    },
  );
}

async function loadMatches(): Promise<void> {
  const container = document.getElementById('friends-list')!;

  try {
    const response = await matchesApi.getAll();
    matches = response.data;
    typingState.clear();

    // Cache distances for profile view
    for (const m of matches) {
      if (m.distance_km != null) store.setDistance(m.user_id, m.distance_km);
    }

    if (matches.length === 0) {
      container.innerHTML = emptyState({
        icon: 'user-friends',
        title: 'No friends yet',
        message: 'Keep discovering to connect with people',
      });
      return;
    }

    renderList();

  } catch (err: any) {
    container.innerHTML = errorState({
      title: 'Failed to load friends',
      retryId: 'retry-btn',
    });
    document.getElementById('retry-btn')?.addEventListener('click', loadMatches);
  }
}

function sortMatches(list: Match[]): Match[] {
  const sorted = [...list];
  const nameTiebreak = (a: Match, b: Match) => a.first_name.localeCompare(b.first_name);

  sorted.sort((a, b) => {
    let cmp = 0;

    switch (currentSort) {
      case 'name':
        cmp = a.first_name.localeCompare(b.first_name);
        break;
      case 'age':
        cmp = a.age - b.age;
        break;
      case 'recent':
        // Sort by last_message_at timestamp (nulls last)
        const timeA = a.last_message_at ? new Date(a.last_message_at).getTime() : 0;
        const timeB = b.last_message_at ? new Date(b.last_message_at).getTime() : 0;
        cmp = timeA - timeB;
        break;
      case 'distance':
        // Sort by distance_km (nulls last)
        const distA = a.distance_km ?? Infinity;
        const distB = b.distance_km ?? Infinity;
        cmp = distA - distB;
        break;
      case 'messages':
        cmp = (a.message_count ?? 0) - (b.message_count ?? 0);
        break;
      case 'answered':
        cmp = (a.qa_answer_count ?? 0) - (b.qa_answer_count ?? 0);
        break;
    }

    // Alphabetical name tiebreak for stable ordering when primary values tie
    if (cmp === 0 && currentSort !== 'name') cmp = nameTiebreak(a, b);

    return currentDirection === 'asc' ? cmp : -cmp;
  });

  return sorted;
}

function renderList(): void {
  const container = document.getElementById('friends-list')!;
  const sorted = sortMatches(matches);

  container.innerHTML = `
    <div class="friends-list">
      ${sorted.map(match => renderMatchItem(match)).join('')}
    </div>
  `;

  // Add click handlers
  sorted.forEach(match => {
    const item = document.getElementById(`match-${match.user_id}`);
    if (item) {
      item.addEventListener('click', () => handleMatchClick(match));
    }
  });
}

function renderMatchItem(match: Match): string {
  const typingText = typingState.get(match.user_id);

  let preview: string;
  if (typingText) {
    preview = truncate(typingText, 200);
  } else if (match.last_message) {
    preview = truncate(stripQaReplyPrefix(match.last_message), 200);
  } else {
    preview = 'Tap to start chatting';
  }

  const infoStr = match.city || '';

  // Build sort-contextual detail text (right side of the dot)
  let detailText = '';
  switch (currentSort) {
    case 'recent':
      detailText = match.last_message_at ? formatTimeAgo(match.last_message_at) : '';
      break;
    case 'distance':
      detailText = match.distance_km != null ? formatDistance(match.distance_km, getUnits()) : '';
      break;
    case 'age':
      detailText = String(match.age);
      break;
    case 'messages':
      detailText = `${match.message_count ?? 0} msgs`;
      break;
    case 'answered':
      detailText = `${match.qa_answer_count ?? 0} answered`;
      break;
    // 'name' - no detail text
  }

  let metaHtml = '';
  if (infoStr) metaHtml += `<span class="friend-info">${escapeHtml(infoStr)}</span>`;
  if (infoStr && detailText) metaHtml += `<span class="friend-dot">·</span>`;
  if (detailText) metaHtml += `<span class="friend-time">${detailText}</span>`;

  return renderListItem({
    id: `match-${match.user_id}`,
    avatarUrl: match.avatar_url,
    avatarFallbackIcon: 'user',
    title: match.first_name,
    metaHtml,
    preview,
    unreadCount: match.unread_count,
  });
}

async function handleMatchClick(match: Match): Promise<void> {
  // Save scroll position before navigating to chat
  const list = document.getElementById('friends-list');
  if (list) savedScrollPosition = list.scrollTop;

  if (match.chat_id) {
    navigate(`/chat/${match.chat_id}/answers`);
    return;
  }

  // No chat exists yet - create one
  try {
    const response = await chatsApi.create(match.user_id);
    const chatId = response.data.id;
    match.chat_id = chatId; // Update local state
    navigate(`/chat/${chatId}/answers`);
  } catch (err: any) {
    showApiError(err, API_ERR_START_CHAT);
  }
}

// Socket event handlers
function handleNewMessage(data: { chatId: string; message: any }): void {
  const match = matches.find(m => m.chat_id === data.chatId);
  if (!match) return;

  const currentUserId = store.getState().user?.id;
  const isFromOther = data.message.sender_id !== currentUserId;

  // Cancel any pending typing clear timeout (message arrived, so no flash)
  const pendingTimeout = typingClearTimeouts.get(match.user_id);
  if (pendingTimeout) {
    clearTimeout(pendingTimeout);
    typingClearTimeouts.delete(match.user_id);
  }

  // Update match data with timestamp
  match.last_message = data.message.content;
  match.last_message_at = data.message.created_at || new Date().toISOString();

  // Clear typing state for this user (message arrived, replaces typing)
  typingState.delete(match.user_id);

  // Increment unread if from other user and we're on the matches page
  if (isFromOther && getCurrentPath() === '/matches') {
    match.unread_count = (match.unread_count || 0) + 1;
  }

  // Re-render just this item for efficiency
  updateMatchItem(match);
}

function handleTyping(data: { chatId: string; userId: string; text: string }): void {
  // Only show typing from other users
  const currentUserId = store.getState().user?.id;
  if (data.userId === currentUserId) return;

  const match = matches.find(m => m.chat_id === data.chatId);
  if (!match) return;

  typingState.set(match.user_id, data.text);
  updateMatchItem(match);
}

function handleTypingClear(data: { chatId: string; userId: string }): void {
  // Only process if from other user
  const currentUserId = store.getState().user?.id;
  if (data.userId === currentUserId) return;

  const match = matches.find(m => m.chat_id === data.chatId);
  if (!match) return;

  // Only process if user was typing
  if (!typingState.has(match.user_id)) return;

  // Delay clearing typing state to avoid flash when message arrives right after
  // If message:new arrives within 500ms, it cancels this timeout
  const existingTimeout = typingClearTimeouts.get(match.user_id);
  if (existingTimeout) {
    clearTimeout(existingTimeout);
  }

  const timeoutId = window.setTimeout(() => {
    typingClearTimeouts.delete(match.user_id);
    if (typingState.has(match.user_id)) {
      typingState.delete(match.user_id);
      updateMatchItem(match);
    }
  }, 500);

  typingClearTimeouts.set(match.user_id, timeoutId);
}

function updateMatchItem(match: Match): void {
  const existingItem = document.getElementById(`match-${match.user_id}`);
  if (!existingItem) return;

  const typingText = typingState.get(match.user_id);

  existingItem.classList.toggle('unread', match.unread_count > 0);

  // Update preview text
  const previewEl = existingItem.querySelector('.friend-preview');
  if (previewEl) {
    if (typingText) {
      previewEl.textContent = truncate(typingText, 200);
    } else if (match.last_message) {
      previewEl.textContent = truncate(stripQaReplyPrefix(match.last_message), 200);
    } else {
      previewEl.textContent = 'Tap to start chatting';
    }
  }

  // Update unread badge (placed after name, before friend-meta)
  const topRow = existingItem.querySelector('.friend-top-row');
  if (topRow) {
    let badgeEl = topRow.querySelector('.unread-badge');
    const nameEl = topRow.querySelector('.friend-name');

    if (match.unread_count > 0) {
      if (!badgeEl) {
        badgeEl = document.createElement('span');
        badgeEl.className = 'unread-badge';
        if (nameEl && nameEl.nextSibling) {
          topRow.insertBefore(badgeEl, nameEl.nextSibling);
        } else {
          topRow.appendChild(badgeEl);
        }
      }
      badgeEl.textContent = String(match.unread_count);
    } else if (badgeEl) {
      badgeEl.remove();
    }
  }

  // Update time display only when sorting by recent
  if (currentSort === 'recent') {
    const timeEl = existingItem.querySelector('.friend-time');
    if (timeEl && match.last_message_at) {
      timeEl.textContent = formatTimeAgo(match.last_message_at);
    }
  }
}

function setupSocketListeners(): void {
  socket.onNewMessage(handleNewMessage);
  socket.onTyping(handleTyping);
  socket.onTypingClear(handleTypingClear);
}

function startTimeUpdateInterval(): void {
  // Update time displays every 30 seconds
  if (timeUpdateInterval) {
    clearInterval(timeUpdateInterval);
  }
  timeUpdateInterval = window.setInterval(() => {
    // Only update time displays when sorting by recent
    if (currentSort !== 'recent') return;
    matches.forEach(match => {
      if (match.last_message_at) {
        const timeEl = document.querySelector(`#match-${match.user_id} .friend-time`);
        if (timeEl) {
          timeEl.textContent = formatTimeAgo(match.last_message_at);
        }
      }
    });
  }, 30000);
}

function stopTimeUpdateInterval(): void {
  if (timeUpdateInterval) {
    clearInterval(timeUpdateInterval);
    timeUpdateInterval = null;
  }
}

// Cleanup when leaving matches view
window.addEventListener('hashchange', () => {
  if (!getCurrentPath().startsWith('/matches')) {
    // Remove socket listeners
    socket.off('message:new', handleNewMessage);
    socket.off('message:typing', handleTyping);
    socket.off('message:typing:clear', handleTypingClear);
    // Clear all pending typing clear timeouts
    typingClearTimeouts.forEach(timeoutId => clearTimeout(timeoutId));
    typingClearTimeouts.clear();
    typingState.clear();
    stopTimeUpdateInterval();
    // Clear saved scroll position if not going to a chat
    if (!getCurrentPath().startsWith('/chat/')) {
      savedScrollPosition = null;
    }
  }
});
